import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import { MenuItem, Popover } from '@mui/material';
import { addDays, format, getDay, isBefore, startOfDay, isSameDay } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../../context/SettingsContext';
import AddRosterItemsDialog from './AddRosterItemsDialog';
import RemoveRosterItemDialog from './RemoveRosterItemDialog';
import { toast } from 'react-toastify';
import { addRosterItem } from '../../../axios/RosterItem/RosterItemApi';
import EditRosterItemsDialog from './EditRosterItemsDialog';
import { useAuth } from '../../../context/AuthContext';
import AddShiftRequestDialog from './AddShiftRequestDialog';
import { SpinnerRing } from '../../../components/CustomSpinner';
import RefreshCircleIcon20pxSvgV2 from "../../../assets/images/icons/RefreshCircleIcon20pxSvgV2";
import RefreshCircleIcon14pxSvgV2 from '../../../assets/images/icons/RefreshCircleIcon14pxSvgV2';
import { Tooltip, Box } from "@mui/material";
import { useNavigate } from 'react-router-dom';

const TOUR_FAKE_USER = {
   userId: "tour-fake-user",
   fullName: "Sample User",
};

const ClassicView = ({ selectedRows, activeWeek, usersData, shiftsData, rosters, onRefresh, updateRoster, setSelectedShiftDateTime, genaralSettingDateFormat, refreshData, shiftColorMap, usersDataByGroup, GroupData, allUser, handleRefreshData, rosterId, startDate, loadingRoster }) => {
   const [anchorEl, setAnchorEl] = useState(null);
   const tourItemAnchorRef = useRef(null);
   const { settings } = useSettings();
   const { t } = useTranslation();
   const [openAddRosterItems, setOpenAddRosterItems] = useState(false);
   const [removeRosterItemDialogOpen, setRemoveRosterItemDialogOpen] = useState(false);
   const [selectedRosterItem, setSelectedRosterItem] = useState({});
   const [editRosterItemDialogOpen, setEditRosterItemDialogOpen] = useState(false);
   const [tourForceShowAddCell, setTourForceShowAddCell] = useState(false);

   const isToday = (dateStr) => {
      if (!dateStr) return false;

      return isSameDay(new Date(dateStr), new Date());
   };

   const isPastDate = (dateStr) => {
      if (!dateStr) return false;

      const today = startOfDay(new Date());
      const compareDate = startOfDay(new Date(dateStr));

      return isBefore(compareDate, today);
   };

   const navigate = useNavigate();
   const [selectedItem, setSelectedItem] = useState({
      day: '',
      week: 1,
      startDate: '',
   });
   const { isEmployee, profile, isAdmin } = useAuth();
   const dayName = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
   const [selectedDate, setSelectedDate] = useState(null);

   const renderSwapTooltip = (rosterItem) => {

      const data = rosterItem?.requestNewData;
      if (!data) return "";

      const fromUser = allUser?.find(u => u.userId === data.userId);
      const toUser = allUser?.find(u => u.userId === data.toUserId);

      const fromShift = shiftsData?.find(s => s.shiftId === data.fromShiftId);
      const toShift = shiftsData?.find(s => s.shiftId === data.toShiftId);

      const fromGroup = GroupData?.find(u => u.id === data.fromGroupId);
      const toGroup = GroupData?.find(u => u.id === data.toGroupId);

      const formatTime = (timeStr) => {
         if (!timeStr) return "";

         const [hours, minutes] = timeStr.split(":").map(Number);

         const displayHours =
            hours >= 24
               ? String(hours - 24).padStart(2, "0")
               : String(hours).padStart(2, "0");

         const suffix = hours >= 24 ? ` ${t("common.next-day")}` : "";

         return `${displayHours}:${String(minutes).padStart(2, "0")}${suffix}`;
      };

      return (
         <Box className="text-sm p-1 flex flex-col gap-1">
            <p className="font-medium">
               {fromUser?.fullName
                  ? fromUser.fullName
                  : fromGroup?.groupName
                     ? fromGroup.groupName
                     : data.userId}
            </p>

            <p className="text-[#6B7280]">
               ({t(`common.${rosterItem?.day?.toLowerCase()}`)}, {format(new Date(rosterItem?.weekDate), genaralSettingDateFormat)})
            </p>

            <p>
               {fromShift?.shiftName || data.fromShiftId}{" "}
               <span className="text-[#6B7280]">
                  ({formatTime(fromShift?.shiftStart)} - {formatTime(fromShift?.shiftEnd)})
               </span>
            </p>

            <p>
               {toShift?.shiftName || data.toShift}{" "}
               <span className="text-[#6B7280]">
                  ({formatTime(toShift?.shiftStart)} - {formatTime(toShift?.shiftEnd)})
               </span>
            </p>

            <p className="text-[#6B7280]">
               {t("common.swapped-with")}{" "}
               <span className="font-medium text-black">
                  {toUser?.fullName
                     ? toUser.fullName
                     : toGroup?.groupName
                        ? toGroup.groupName
                        : data.toUserId}
               </span>
            </p>

            <p
               className="text-blue-600 cursor-pointer mt-1"
               onClick={() => {
                  navigate(`/controlpanel/shift-and-roster-management?requestShiftId=${rosterItem?.swapRequestId}`);
               }}
            >
               {t("button.detail")}
            </p>
         </Box>
      );
   };

   const classicView = "classicView";
   const handleClick = (event, item, weekDate) => {
      setAnchorEl(event.currentTarget);
      setSelectedRosterItem(item);
      setSelectedDate(weekDate);
   };

   const handleClose = () => {
      setAnchorEl(null);
   };

   const handleOpenRemoveRosterItem = () => {
      setRemoveRosterItemDialogOpen(true);
   };

   const handleOpenEditRosterItem = () => {
      setEditRosterItemDialogOpen(true);
   }

   const handleCloseRemoveRosterItem = () => {
      setRemoveRosterItemDialogOpen(false);
   };

   const [openAddShiftEvent, setOpenAddShiftEvent] = useState(false);

   const handleOpenAddShiftEvent = () => {
      setOpenAddShiftEvent(true);
   };

   const handleCloseAddShiftEvent = () => {
      setOpenAddShiftEvent(false);
   };

   const handleOpenAddRosterItems = (userId, startDateData, groupId, groupName) => {
      const clickedDate = startOfDay(new Date(startDateData));
      const rosterStart = selectedRows?.startDate
         ? startOfDay(new Date(selectedRows.startDate))
         : null;
      const rosterEnd = selectedRows?.endBy
         ? startOfDay(new Date(selectedRows.endBy))
         : null;

      if (
         (rosterStart && clickedDate < rosterStart) ||
         (rosterEnd && clickedDate > rosterEnd)
      ) {
         toast.error(t("common.date-out-of-roster-range"));
         return;
      }

      const dayIndex = getDay(startDateData);
      const dayText = t(`days.${dayIndex}`);

      setSelectedItem({
         rosterId: selectedRows?.rosterId || rosterId,
         userId: userId,
         startDate: new Date(startDateData),
         day: dayText,
         groupId: groupId,
         groupName: groupName
      });
      setOpenAddRosterItems(true);
   };


   const handleCloseAddRosterItems = () => {
      setOpenAddRosterItems(false);
   };

   const handleCloseEditRosterItems = () => {
      setEditRosterItemDialogOpen(false);
      setAnchorEl(null);
   };

   // Tour: fake table + mở menu/dialog khi chưa có dữ liệu
   useEffect(() => {
      const getTourFakeItem = () => {
         const todayStr = format(startOfDay(new Date()), "yyyy-MM-dd");
         const existing = (rosters || []).find(
            (r) => r.userId != null || r.groupId != null
         );
         if (existing) return existing;
         return {
            rosterItemId: "tour-fake-item",
            userId: "tour-fake-user",
            shiftId: "tour-fake-shift",
            shiftName: "Morning",
            shiftTime: "08:00 - 17:00",
            weekDate: todayStr,
            day: "Monday",
            isSwapShift: false,
            isEnabled: true,
         };
      };

      const closeTourItemDialogs = () => {
         setOpenAddRosterItems(false);
         setEditRosterItemDialogOpen(false);
         setRemoveRosterItemDialogOpen(false);
         setOpenAddShiftEvent(false);
         setAnchorEl(null);
         setTourForceShowAddCell(false);
      };

      window.__openTourClassicItemMenu = () => {
         flushSync(() => {
            closeTourItemDialogs();
            const item = getTourFakeItem();
            setSelectedRosterItem(item);
            setSelectedDate(item.weekDate);
            setAnchorEl(tourItemAnchorRef.current);
         });
      };

      window.__openTourAddRequestDialog = () => {
         flushSync(() => {
            setEditRosterItemDialogOpen(false);
            setRemoveRosterItemDialogOpen(false);
            setOpenAddRosterItems(false);
            setAnchorEl(null);
            setSelectedRosterItem(getTourFakeItem());
            setOpenAddShiftEvent(true);
         });
      };

      window.__closeTourAddRequestDialog = () => {
         flushSync(() => setOpenAddShiftEvent(false));
      };

      window.__openTourEditRosterItemDialog = () => {
         flushSync(() => {
            setOpenAddShiftEvent(false);
            setRemoveRosterItemDialogOpen(false);
            setOpenAddRosterItems(false);
            setAnchorEl(null);
            setSelectedRosterItem(getTourFakeItem());
            setEditRosterItemDialogOpen(true);
         });
      };

      window.__closeTourEditRosterItemDialog = () => {
         flushSync(() => setEditRosterItemDialogOpen(false));
      };

      window.__openTourDeleteRosterItemDialog = () => {
         flushSync(() => {
            setOpenAddShiftEvent(false);
            setEditRosterItemDialogOpen(false);
            setOpenAddRosterItems(false);
            setAnchorEl(null);
            setSelectedRosterItem(getTourFakeItem());
            setRemoveRosterItemDialogOpen(true);
         });
      };

      window.__closeTourDeleteRosterItemDialog = () => {
         flushSync(() => setRemoveRosterItemDialogOpen(false));
      };

      window.__openTourAddRosterItemFromCell = () => {
         flushSync(() => {
            closeTourItemDialogs();
            setTourForceShowAddCell(true);
         });
      };

      return () => {
         delete window.__openTourClassicItemMenu;
         delete window.__openTourAddRequestDialog;
         delete window.__closeTourAddRequestDialog;
         delete window.__openTourEditRosterItemDialog;
         delete window.__closeTourEditRosterItemDialog;
         delete window.__openTourDeleteRosterItemDialog;
         delete window.__closeTourDeleteRosterItemDialog;
         delete window.__openTourAddRosterItemFromCell;
      };
   }, [rosters]);


   const handleDragStart = (e, rosterItem) => {
      e.dataTransfer.setData('application/json', JSON.stringify(rosterItem));
   };


   // const handleDrop = (e, targetUserId, targetWeekDate) => {
   const handleDrop = async (e, targetUserId, targetWeekDate, endBy, groupId) => {
      e.preventDefault();
      const data = JSON.parse(e.dataTransfer.getData('application/json'));

      const date = new Date(targetWeekDate);
      let nameDay;
      const getDayName = dayName[date.getDay()];
      const submitData = {
         rosterId: selectedRows.rosterId,
         userId: targetUserId,
         startDate: targetWeekDate,
         endDate: endBy ? targetWeekDate : null,
         week: activeWeek + 1,
         day: getDayName,
         shift: data.shiftId,
         groupId: groupId,
         langId: localStorage.getItem("i18nextLngId")
      };


      try {
         const response = await addRosterItem(submitData);

         if (response.data.message === 'Successful' || response.data.message === 'Thành công') {
            toast.success(t("common.created-roster-item-successfully"));
            onRefresh();
         } else {
            toast.error(response.data.message);
         }
      } catch (error) {
         if (error.response) {
            if (error.response.status !== 401) {
               toast.error(t(error?.response.data));
            }
         }
         console.error('There was an error creating the roster item!', error);
      }

   };

   const days = useMemo(() => {
      if (!startDate) return [];

      const rangeStart = startOfDay(new Date(startDate));
      const weekStart = addDays(rangeStart, (activeWeek || 0) * 7);

      return Array.from({ length: 7 }, (_, i) => ({
         weekDate: format(addDays(weekStart, i), "yyyy-MM-dd"),
      }));
   }, [startDate, activeWeek]);

   const rosterRows = useMemo(() => {
      const map = new Map();

      // ================= ADMIN =================
      if (isAdmin) {
         if (!usersData) return [];

         usersData.forEach(item => {
            if (item.groupId && item.groupName) {
               const key = `group-${item.groupId}`;
               if (!map.has(key)) {
                  map.set(key, {
                     type: "group",
                     groupId: item.groupId,
                     groupName: item.groupName,
                  });
               }
            }

            if (item.userId && item.fullName) {
               const key = `user-${item.userId}`;
               if (!map.has(key)) {
                  map.set(key, {
                     type: "user",
                     userId: item.userId,
                     fullName: item.fullName,
                  });
               }
            }
         });
      }

      // ================= EMPLOYEE =================
      else {
         if (profile?.userId) {
            // 1. Add chính user
            map.set(`user-${profile.userId}`, {
               type: "user",
               userId: profile.userId,
               fullName: profile.fullName,
            });

            // 2. Lấy group mà user thuộc
            // const userGroups = usersData?.filter(
            //    (u) => u.userId === profile.userId && u.groupId
            // );

            const userGroups = usersDataByGroup?.filter(
               (g) => Number(g.id) === Number(profile.userId)
            );

            userGroups?.forEach((g) => {
               const key = `group-${g.groupId}`;
               if (!map.has(key)) {
                  const groupInfo = GroupData?.find(gr => gr.id === g.groupId);

                  map.set(key, {
                     type: "group",
                     groupId: g.groupId,
                     groupName: groupInfo?.groupName || `Group ${g.groupId}`,
                  });
               }
            });
         }
      }

      // Tour: nếu chưa có row nào thì thêm user giả
      if (map.size === 0 && window.__tourIsOpen) {
         map.set(`user-${TOUR_FAKE_USER.userId}`, {
            type: "user",
            userId: TOUR_FAKE_USER.userId,
            fullName: TOUR_FAKE_USER.fullName,
         });
      }

      // Tour: thêm user trống để luôn có ô "+" demo
      if (tourForceShowAddCell && !map.has("user-tour-fake-user-empty")) {
         map.set("user-tour-fake-user-empty", {
            type: "user",
            userId: "tour-fake-user-empty",
            fullName: "Sample User",
         });
      }

      // ================= SORT =================
      return Array.from(map.values()).sort((a, b) => {
         if (a.type !== b.type) {
            return a.type === "group" ? -1 : 1;
         }

         const nameA = (a.groupName || a.fullName || "").toLowerCase();
         const nameB = (b.groupName || b.fullName || "").toLowerCase();

         return nameA.localeCompare(nameB);
      });

   }, [isAdmin, usersData, rosters, profile?.userId, usersDataByGroup, GroupData, tourForceShowAddCell]);

   const usersDataNoGroup = useMemo(() => {
      if (!usersData) return [];

      const map = new Map();

      usersData.forEach(item => {
         if (item.userId && !map.has(item.userId)) {
            map.set(item.userId, item);
         }
      });

      return Array.from(map.values());
   }, [usersData]);

   const groupsDataOnly = useMemo(() => {
      if (!usersData) return [];

      const map = new Map();

      usersData.forEach(item => {
         if (item.groupId && !map.has(item.groupId)) {
            map.set(item.groupId, item);
         }
      });

      return Array.from(map.values());
   }, [usersData]);

   return (
      <div className='classic-view'>
         {/* Anchor ẩn để tour mở Popover khi chưa có item thật */}
         <div
            ref={tourItemAnchorRef}
            className="absolute w-1 h-1 opacity-0 pointer-events-none"
            aria-hidden
         />
         {/* <table className='w-full table-fixed' style={{ borderSpacing: '10px' }}> */}
         <div
            className="w-full max-w-full overflow-x-auto"
            data-tour="shift-and-roster-table"
         >
            <table className="w-full md:table-fixed table-auto">
               <thead>
                  {/* <tr className="sticky top-0 z-10 bg-white shadow [&_th]:border [&_th]:border-slate-200 [&_th]:font-semibold [&_th]:text-start [&_th]:h-[53px]"> */}
                  <tr className="bg-white shadow [&_th]:border [&_th]:border-slate-200 [&_th]:font-semibold [&_th]:text-start [&_th]:h-[53px]">

                     <th key='user-group' className='bg-[#F5F8FF] p-2.5'>
                        {t('common.content-163')}
                     </th>
                     {days.map((day, index) => {
                        if (!day?.weekDate) {
                           return <th key={`empty-${index}`} className="bg-[#F5F8FF] p-2.5"></th>;
                        }

                        const parsedDate = new Date(day.weekDate);

                        return (
                           <th
                              key={day.weekDate}
                              className="bg-[#F5F8FF] p-2.5"
                              style={
                                 isToday(day.weekDate)
                                    ? { borderBottom: '2px solid #0EA5E9' }
                                    : {}
                              }
                           >
                              <p className="font-semibold">
                                 {format(parsedDate, genaralSettingDateFormat)}
                              </p>
                              <p className="font-semibold text-[#6B7280]">
                                 {t(`days.${getDay(parsedDate)}`)}
                              </p>
                           </th>
                        );
                     })}
                  </tr>
               </thead>
               <tbody>
                  {loadingRoster ? (
                     <tr>
                        <td colSpan={days.length + 1} className="text-center py-10">
                           <div className="flex justify-center items-center">
                              <SpinnerRing loading={true} size={40} color="#3B82F6" />
                           </div>
                        </td>
                     </tr>
                  ) : rosterRows.length === 0 ? (
                     <tr>
                        <td colSpan={days.length + 1} className="text-center py-10 text-slate-500">
                           {t('common.no-data')}
                        </td>
                     </tr>
                  ) : (
                     rosterRows
                        .filter((u) => {
                           return true;
                        })
                        .map(({ userId, groupId, groupName, firstName, lastName, fullName }) => (
                           <tr
                              key={userId !== null ? userId : groupId}
                              className='[&_td]:border [&_td]:border-slate-200 [&_td]:border-r-0 [&_td]:p-2.5 [&_td:last-child]:border-r [&_td]:h-[45px] '
                           >
                              <td>
                                 <div className="flex items-center gap-2.5">
                                    {userId ? <PersonIcon /> : <GroupIcon />}
                                    <p className="font-medium break-words">
                                       {userId
                                          ? fullName
                                          : groupName === "All users"
                                             ? t("common.all-user")
                                             : groupName}
                                    </p>
                                 </div>
                              </td>

                              {days.map(({ weekDate }) => {
                                 if (!weekDate) return <td key={Math.random()}></td>;

                                 const rosterItemsForDay = rosters.filter((item) => {
                                    if (userId != null) {
                                       return item.userId === userId && item.weekDate === weekDate;
                                    } else {
                                       return item.groupId === groupId && item.weekDate === weekDate;
                                    }
                                 });

                                 // const rosterItemsForDay = rosters.filter(
                                 //    (item) => item.userId === userId && item.weekDate === weekDate
                                 // );

                                 return (
                                    <td
                                       key={weekDate}
                                       className={`align-top group relative 
    ${rosterItemsForDay.length > 0 ? 'hover:bg-[#EFF6FF]' : ''}
    ${isToday(weekDate) ? 'bg-[#F0F9FF]' : ''}
  `}
                                       onDragOver={(e) => e.preventDefault()}
                                       onDrop={(e) => {
                                          if (isEmployee) return;
                                          e.preventDefault();
                                          const data = e.dataTransfer.getData("application/json");
                                          const draggedItem = JSON.parse(data);
                                          const endDate = draggedItem.endDate || null;

                                          handleDrop(e, userId, weekDate, endDate, groupId);
                                       }}
                                    >
                                       <div className='h-full flex flex-col justify-between'>
                                          {rosterItemsForDay.length > 0 ? (
                                             <div className='flex flex-col gap-2'>
                                                {rosterItemsForDay.map((rosterItem) => {
                                                   const shiftColor =
                                                      shiftColorMap?.[rosterItem.shiftId] || '#93C5FD';

                                                   const content = (
                                                      <div
                                                         key={rosterItem.rosterItemId}
                                                         className='py-2.5 px-4 rounded-md shadow-roster min-h-[42px] border-l-8'
                                                         style={{ borderLeftColor: shiftColor }}
                                                         draggable={!isEmployee && !rosterItem.isSwapShift}
                                                         onDragStart={(e) => {
                                                            if (isEmployee) return;
                                                            e.dataTransfer.setData(
                                                               "application/json",
                                                               JSON.stringify(rosterItem)
                                                            );
                                                            handleDragStart(e, rosterItem);
                                                         }}
                                                      >
                                                         <div className='flex items-center justify-between'>
                                                            <p className='font-medium'>{rosterItem.shiftName}</p>

                                                            {!rosterItem.isSwapShift &&
                                                               !(isEmployee && isPastDate(weekDate)) && (
                                                                  <MoreVertIcon
                                                                     fontSize='small'
                                                                     sx={{ fontSize: 15, cursor: 'pointer' }}
                                                                     onClick={(event) => handleClick(event, rosterItem, weekDate)}
                                                                  />
                                                               )}
                                                         </div>

                                                         {/* shift time */}
                                                         {(() => {


                                                            const [startRaw, endRaw] = rosterItem.shiftTime.split(' - ');

                                                            const formatTime = (timeStr) => {
                                                               const [hours, minutes] = timeStr.split(':').map(Number);

                                                               const displayHours =
                                                                  hours >= 24
                                                                     ? String(hours - 24).padStart(2, '0')
                                                                     : String(hours).padStart(2, '0');

                                                               const suffix = hours >= 24 ? t('common.next-day') : '';

                                                               return `${displayHours}:${String(minutes).padStart(2, '0')}${suffix}`;
                                                            };

                                                            return (
                                                               <p className="font-normal">
                                                                  {formatTime(startRaw)} - {formatTime(endRaw)}
                                                               </p>
                                                            );
                                                         })()}

                                                         {rosterItem?.isSwapShift && (
                                                            <div className="text-[#0057FF] flex gap-1 items-center">
                                                               <RefreshCircleIcon14pxSvgV2 />
                                                               <p>{t('common.swapped')}</p>
                                                            </div>
                                                         )}
                                                      </div>
                                                   );

                                                   if (rosterItem?.isSwapShift) {
                                                      return (
                                                         <Tooltip
                                                            title={renderSwapTooltip(rosterItem)}
                                                            arrow
                                                            placement="right"
                                                            slotProps={{
                                                               tooltip: {
                                                                  sx: {
                                                                     backgroundColor: "#ffffff",
                                                                     color: "#000000",
                                                                     border: "1px solid #E5E7EB",
                                                                     boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                                                     fontSize: "13px",
                                                                  }
                                                               },
                                                               arrow: {
                                                                  sx: {
                                                                     color: "#ffffff"
                                                                  }
                                                               }
                                                            }}
                                                         >
                                                            {content}
                                                         </Tooltip>
                                                      );
                                                   }

                                                   return content;
                                                })}
                                             </div>
                                          ) : (
                                             (!isEmployee || tourForceShowAddCell) && (
                                                <div
                                                   data-tour="shift-and-roster-add-rosteritem-table"
                                                   className={`px-4 h-full bg-[#EFF6FF] border border-dashed border-[#3B82F6] rounded-[6px] items-center justify-center cursor-pointer ${
                                                      tourForceShowAddCell || window.__tourIsOpen
                                                         ? "flex"
                                                         : "hidden group-hover:flex"
                                                   }`}
                                                   onClick={() => {
                                                      if (isEmployee) return;
                                                      handleOpenAddRosterItems(userId, weekDate, groupId, groupName);
                                                   }}
                                                >
                                                   <span className='text-[#3B82F6] text-[40px] font-thin flex items-center justify-center leading-5'>
                                                      +
                                                   </span>
                                                </div>
                                             )
                                          )}
                                          {!isEmployee && rosterItemsForDay.length > 0 && (
                                             <div
                                                className='w-6 h-6 border border-dashed border-[#3B82F6] rounded-[6px] flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-200 ml-auto mr-1 mt-2'
                                                // className="absolute bottom-2 right-2 w-6 h-6 border border-dashed border-[#3B82F6] rounded-[6px] flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                                onClick={() => handleOpenAddRosterItems(userId, weekDate, groupId, groupName)}
                                             >
                                                <span className='text-[#3B82F6] text-[16px] leading-none'>+</span>
                                             </div>
                                          )}
                                       </div>
                                    </td>
                                 );
                              })}
                           </tr>
                        ))
                  )}
               </tbody>
            </table>
         </div>


         <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
               vertical: 'bottom',
               horizontal: 'left',
            }}
            PaperProps={{
               sx: {
                  borderRadius: '8px',
                  padding: '8px', // tương đương p-2
               },
            }}
         >
            {!selectedRosterItem?.isSwapShift &&
               !isPastDate(selectedDate) && (
                  <MenuItem
                     data-tour="shift-and-roster-add-request"
                     className='gap-3 items-center h-9 hover:!bg-blue-100'
                     style={{ padding: '8px 12px', borderRadius: 6 }}
                     onClick={handleOpenAddShiftEvent}
                  >
                     <p>{t('common.add-requests')}</p>
                  </MenuItem>
               )}
            {isAdmin && (
               <>
                  <MenuItem
                     data-tour="shift-and-roster-edit-item"
                     className='gap-3 items-center h-9 hover:!bg-blue-100'
                     style={{ padding: '8px 12px', borderRadius: 6 }}
                     onClick={handleOpenEditRosterItem}
                  >
                     <p>{t('button.edit')}</p>
                  </MenuItem>
                  <MenuItem
                     data-tour="shift-and-roster-delete-item"
                     className='gap-3 items-center h-9 hover:!bg-blue-100'
                     style={{ padding: '8px 12px', borderRadius: 6 }}
                     onClick={handleOpenRemoveRosterItem}
                  >
                     <p>{t('button.delete')}</p>
                  </MenuItem></>
            )}
         </Popover>


         {removeRosterItemDialogOpen && <RemoveRosterItemDialog onRefresh={onRefresh} selectedRosterItem={selectedRosterItem}
            onClose={handleCloseRemoveRosterItem}
            anchorEl={setAnchorEl} />}
         {openAddRosterItems && <AddRosterItemsDialog onRefresh={onRefresh} shiftsData={shiftsData} usersData={usersData} selectedRosterItem={selectedRosterItem} selectedItem={selectedItem} genaralSettingDateFormat={genaralSettingDateFormat} page={classicView}
            activeWeek={activeWeek} selectedRows={selectedRows}
            onClose={handleCloseAddRosterItems} />}
         {editRosterItemDialogOpen && <EditRosterItemsDialog onRefresh={onRefresh} shiftsData={shiftsData} selectedRosterItem={selectedRosterItem} anchorEl={setAnchorEl} genaralSettingDateFormat={genaralSettingDateFormat}
            selectedRows={selectedRows} onClose={handleCloseEditRosterItems} />}

         {openAddShiftEvent && (
            <AddShiftRequestDialog
               open={openAddShiftEvent}
               onClose={handleCloseAddShiftEvent}
               usersData={allUser}
               shiftsData={shiftsData}
               onRefresh={handleRefreshData}
               selectedDate={selectedDate}
               rosterItemId={selectedRosterItem?.rosterItemId}
               selectedRosterItem={selectedRosterItem}
               rosterId={rosterId}
               usersDataGroup={usersDataNoGroup}
               usersGroup={groupsDataOnly}
            />
         )}
      </div>
   );
};

export default ClassicView;
